import { useEffect, useState } from "react";
import { NavLink, Navigate, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, IndianRupee, Inbox, Star, LogOut, Loader2, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { LOGO_URL } from "@/lib/brand";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/enquiries", label: "Enquiries", icon: Inbox },
  { to: "/admin/artists", label: "Artists", icon: Users },
  { to: "/admin/services", label: "Services & Rates", icon: IndianRupee },
  { to: "/admin/reviews", label: "Reviews", icon: Star },
];

export default function AdminLayout() {
  const [user, setUser] = useState(undefined);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(null));
  }, []);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    navigate("/admin/login");
  };

  if (user === undefined)
    return (
      <div className="min-h-screen bg-[#1E1D1D] flex items-center justify-center" data-testid="admin-loading">
        <Loader2 className="w-8 h-8 text-velvet animate-spin" />
      </div>
    );
  if (!user) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-[#1E1D1D] text-ivory flex" data-testid="admin-panel">
      <aside className="hidden lg:flex flex-col w-64 border-r border-ivory/10 p-6 sticky top-0 h-screen">
        <img src={LOGO_URL} alt="The Velvet Brush" className="h-12 w-auto bg-ivory rounded-lg p-1 self-start" />
        <p className="mt-2 text-[10px] uppercase tracking-[0.3em] text-ivory/40">Admin Panel</p>
        <nav className="mt-10 flex flex-col gap-1 flex-1" data-testid="admin-nav">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`admin-nav-${n.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl text-sm transition-colors ${
                  isActive ? "bg-velvet text-ink font-semibold" : "text-ivory/60 hover:text-ivory hover:bg-ivory/5"
                }`
              }
            >
              <n.icon className="w-4 h-4" /> {n.label}
            </NavLink>
          ))}
        </nav>
        <a href="/" data-testid="admin-view-site" className="flex items-center gap-2 text-xs text-ivory/40 hover:text-ivory/80 mb-3">
          <ExternalLink className="w-3.5 h-3.5" /> View website
        </a>
        <button onClick={logout} data-testid="admin-logout-btn"
          className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-ivory/60 hover:text-ivory hover:bg-ivory/5 transition-colors">
          <LogOut className="w-4 h-4" /> Logout
        </button>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        <div className="lg:hidden sticky top-0 z-40 bg-[#1E1D1D]/95 backdrop-blur border-b border-ivory/10 px-4 py-3 flex items-center justify-between">
          <span className="font-serif text-lg">Admin</span>
          <button onClick={logout} data-testid="admin-logout-mobile" className="text-ivory/60 p-2"><LogOut className="w-5 h-5" /></button>
        </div>
        <main className="flex-1 p-4 sm:p-8 max-w-6xl w-full mx-auto">
          <Outlet />
        </main>
        <nav className="lg:hidden sticky bottom-0 bg-[#1E1D1D] border-t border-ivory/10 grid grid-cols-5" data-testid="admin-mobile-nav">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`admin-mnav-${n.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className={({ isActive }) =>
                `flex flex-col items-center gap-1 py-3 text-[10px] ${isActive ? "text-velvet" : "text-ivory/40"}`
              }
            >
              <n.icon className="w-5 h-5" />
              {n.label.split(" ")[0]}
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}
